/**
 * The water — a hand-written WebGL fragment shader with no dependency (D-5).
 *
 * Not three.js: ~150 KB gzipped for nothing visible at phone size. This is the
 * same call `HubHero.tsx` already makes in leeseidon.
 *
 * D-4 — the water is DECORATION. Every figure stays readable with this canvas
 * deleted. No state is encoded only here.
 *
 * D-6 — motion on input, never on a clock (amended 2026-10-04). An app opened
 * for three seconds, dozens of times a day, must not animate for three
 * minutes — or for three seconds when nothing changed:
 *
 *   screen opens    → the level eases up to its mark, then freezes
 *   expense logged  → ripple, rise, settle, freeze
 *   preview nudged  → the faint rise-preview line eases, then freezes
 *   back to the app → one still frame; nothing moves
 *   resize / theme  → one still frame
 *   tab hidden      → cancel the RAF loop ENTIRELY
 *   reduced motion  → one still frame at the target, always
 *   no WebGL        → Canvas 2D, same wave function
 *   no canvas       → `tier: "static"`; `<WaterSurface>` draws `<CssWater>`
 *
 * Each input moves the water for at most `MOTION_BUDGET_MS`, and only while it
 * is actually still moving; when it is frozen the RAF loop is not running at
 * all. The rules live in `motion_budget.ts` and the easing in `ease.ts`, both
 * unit-tested; this file draws.
 */

import { type Rgb, parseCssColor } from "@/water/color";
import { easeToward, isSettled } from "@/water/ease";
import { MOTION_BUDGET_MS, createMotionBudget } from "@/water/motion_budget";

const VERTEX_SRC = `
attribute vec2 a;
varying vec2 v;
void main() {
  v = a * 0.5 + 0.5;
  gl_Position = vec4(a, 0.0, 1.0);
}`;

const FRAGMENT_SRC = `
precision mediump float;
varying vec2 v;

uniform float t;    // seconds
uniform float tilt;
uniform float lv;   // surface height, 0..1
uniform float amp;  // ripple multiplier, 1 at rest
uniform float ar;   // aspect ratio, so waves do not stretch on a narrow gauge
uniform float px;   // one device pixel as a fraction of height, for edge AA
uniform vec3  wc;   // water colour
uniform vec3  cc;   // crest colour
uniform float pv;   // rise-preview level, 0..1; negative draws none

// Three summed sines: the surface never visibly repeats. Amplitude ~2.7% of
// height so it reads as water, not a flat line — but calm enough for the motion
// budget (D-6). MUST stay in lock-step with waveAt() below (the Canvas 2D tier).
float wave(float x, float tt) {
  return sin(x *  9.0 + tt * 0.90) * 0.0150
       + sin(x * 16.0 - tt * 1.35) * 0.0080
       + sin(x * 27.0 + tt * 0.55) * 0.0040;
}

void main() {
  float w = wave(v.x * max(ar, 0.35), t);
  float surf = lv + (v.x - 0.5) * tilt + w * amp;
  float d = surf - v.y;

  // The rise preview: a faint line where the amount being typed would take
  // the water — arithmetic on the number, never a projection. It rides a
  // calmer copy of the wave so it reads as a waterline, not a rule.
  float pl = 0.0;
  if (pv >= 0.0) pl = smoothstep(1.5 * px, 0.0, abs(pv + w * 0.6 - v.y)) * 0.55;

  // Well above the feathered surface: transparent but for the preview, so DOM
  // content shows through. The band below is anti-aliased, not hard-cut.
  if (d < -2.0 * px) { gl_FragColor = vec4(cc, pl); return; }

  // Depth darkening — this is what makes it read as volume, not a shape.
  float dep = clamp(d * 2.4, 0.0, 1.0);
  vec3 col = mix(wc * 1.30, wc * 0.52, dep);

  // Caustics: two crossed sine fields, brightest near the surface.
  float c = sin((v.x * 24.0 + v.y * 13.0) + t * 1.10)
          * sin((v.x * 16.0 - v.y * 21.0) - t * 0.72);
  col += cc * smoothstep(0.50, 1.0, c) * 0.13 * (1.0 - dep);

  // The lit crest line.
  float crest = smoothstep(0.010, 0.0, d);
  col = mix(col, cc, crest * 0.90);

  // Feather the surface across ~1.5 device px so the near-horizontal waterline
  // is anti-aliased. antialias:false on the context, and MSAA would not touch a
  // shader-defined alpha edge anyway — so the smoothing has to happen here.
  float cov = smoothstep(-1.5 * px, 1.5 * px, d);
  float alpha = max(mix(0.42, 0.86, dep), crest * 0.95) * cov;
  col = mix(col, cc, pl);
  gl_FragColor = vec4(col, max(alpha, pl));
}`;

const MAX_TILT_SLOPE = 0.04;
const RIPPLE_AMPLITUDE = 3.4;
/** The level closes half its gap in this long: settled in ~2s from empty to full. */
const LEVEL_HALF_LIFE_MS = 220;
/** The preview line follows the typed amount quicker than the water does. */
const PREVIEW_HALF_LIFE_MS = 120;
/** A ripple dies down over ~1.5s. */
const RIPPLE_HALF_LIFE_MS = 300;
/** A ripple this close to calm reads as calm. */
const RIPPLE_EPSILON = 0.02;
/** `pv` for "no preview line". */
const NO_PREVIEW = -1;
/** The water may flood a little past the top: the over-budget state. */
const MAX_LEVEL = 1.06;

export interface WaterColors {
  water: [number, number, number];
  crest: [number, number, number];
}

interface GlContext {
  gl: WebGLRenderingContext;
  uniforms: {
    tilt: WebGLUniformLocation | null;
    t: WebGLUniformLocation | null;
    lv: WebGLUniformLocation | null;
    amp: WebGLUniformLocation | null;
    ar: WebGLUniformLocation | null;
    px: WebGLUniformLocation | null;
    wc: WebGLUniformLocation | null;
    cc: WebGLUniformLocation | null;
    pv: WebGLUniformLocation | null;
  };
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    // Not thrown: a shader failure is a DOWNGRADE, not a crash (D-5). The
    // Canvas 2D tier takes over and the user sees water either way.
    console.warn("Water shader failed to compile:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createGl(canvas: HTMLCanvasElement): GlContext | null {
  const gl = canvas.getContext("webgl", {
    alpha: true,
    premultipliedAlpha: false,
    antialias: false,
    depth: false,
    powerPreference: "low-power",
  });
  if (!gl) return null;

  const vs = compile(gl, gl.VERTEX_SHADER, VERTEX_SRC);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SRC);
  if (!vs || !fs) return null;

  // Context loss may return null even though the DOM declaration omits it.
  const program = gl.createProgram() as WebGLProgram | null;
  if (!program) return null;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn("Water program failed to link:", gl.getProgramInfoLog(program));
    return null;
  }
  gl.useProgram(program);

  // One oversized triangle covers the viewport with fewer vertices than a quad.
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const location = gl.getAttribLocation(program, "a");
  gl.enableVertexAttribArray(location);
  gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  return {
    gl,
    uniforms: {
      tilt: gl.getUniformLocation(program, "tilt"),
      t: gl.getUniformLocation(program, "t"),
      lv: gl.getUniformLocation(program, "lv"),
      amp: gl.getUniformLocation(program, "amp"),
      ar: gl.getUniformLocation(program, "ar"),
      px: gl.getUniformLocation(program, "px"),
      wc: gl.getUniformLocation(program, "wc"),
      cc: gl.getUniformLocation(program, "cc"),
      pv: gl.getUniformLocation(program, "pv"),
    },
  };
}

/** The same wave function as the shader, for the Canvas 2D tier. Keep in sync. */
function waveAt(x: number, t: number): number {
  return (
    Math.sin(x * 9.0 + t * 0.9) * 0.015 +
    Math.sin(x * 16.0 - t * 1.35) * 0.008 +
    Math.sin(x * 27.0 + t * 0.55) * 0.004
  );
}

function draw2d(
  canvas: HTMLCanvasElement,
  level: number,
  amp: number,
  t: number,
  colors: WaterColors,
  preview: number,
  tilt: number,
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  const [wr, wg, wb] = colors.water;
  const [cr, cg, cb] = colors.crest;
  const rgb = (c: readonly [number, number, number]): string =>
    `${String(Math.round(c[0] * 255))},${String(Math.round(c[1] * 255))},${String(Math.round(c[2] * 255))}`;

  const yAt = (x: number): number => h - (level + (x / w - 0.5) * tilt + waveAt(x / w, t) * amp) * h;

  ctx.beginPath();
  ctx.moveTo(0, yAt(0));
  for (let x = 0; x <= w; x += 3) ctx.lineTo(x, yAt(x));
  ctx.lineTo(w, h);
  ctx.lineTo(0, h);
  ctx.closePath();

  const gradient = ctx.createLinearGradient(0, h - level * h, 0, h);
  gradient.addColorStop(0, `rgba(${rgb([wr, wg, wb])},0.42)`);
  gradient.addColorStop(1, `rgba(${rgb([wr, wg, wb])},0.80)`);
  ctx.fillStyle = gradient;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(0, yAt(0));
  for (let x = 0; x <= w; x += 3) ctx.lineTo(x, yAt(x));
  ctx.strokeStyle = `rgb(${rgb([cr, cg, cb])})`;
  ctx.lineWidth = 2;
  ctx.stroke();

  // The rise preview, as in the shader: a faint, calmer waterline.
  if (preview >= 0) {
    const pyAt = (x: number): number => h - (preview + waveAt(x / w, t) * 0.6) * h;
    ctx.beginPath();
    ctx.moveTo(0, pyAt(0));
    for (let x = 0; x <= w; x += 3) ctx.lineTo(x, pyAt(x));
    ctx.strokeStyle = `rgba(${rgb([cr, cg, cb])},0.55)`;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

export interface WaterEngine {
  setTilt(slope: number): void;
  /** Move the surface. `ripple` spikes the wave amplitude for a logged expense. */
  setLevel(level: number, ripple?: boolean): void;
  /** Show the rise-preview line at a level, or hide it with null (Capture). */
  setPreview(level: number | null): void;
  /** Re-read theme colours, e.g. after a light/dark switch. Draws a still frame. */
  refreshColors(): void;
  destroy(): void;
  /** Which tier is actually running — surfaced for diagnostics, not behaviour. */
  readonly tier: "webgl" | "canvas2d" | "static";
}

/**
 * Abyss's water and crest, for a host whose tokens cannot be read (a detached
 * element, a token renamed out from under the engine). The water is decoration
 * (D-4), so it is drawn in a sensible colour rather than not at all.
 */
const FALLBACK_WATER: Rgb = [0x15 / 255, 0x88 / 255, 0x9a / 255];
const FALLBACK_CREST: Rgb = [0x5f / 255, 0xf0 / 255, 0xd8 / 255];

function readColors(host: Element): WaterColors {
  const style = getComputedStyle(host);
  const read = (name: string, fallback: Rgb): [number, number, number] => {
    const [r, g, b] = parseCssColor(style.getPropertyValue(name)) ?? fallback;
    return [r, g, b];
  };
  return {
    water: read("--ae-water", FALLBACK_WATER),
    crest: read("--ae-crest", FALLBACK_CREST),
  };
}

function clampLevel(value: number): number {
  return Math.max(0, Math.min(MAX_LEVEL, value));
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Attach the water to a canvas. `host` is the element the theme tokens are read
 * from — usually the canvas's own parent.
 */
export function createWater(
  canvas: HTMLCanvasElement,
  host: Element,
  initialLevel = 0,
  tierChanged: (tier: WaterEngine["tier"]) => void = () => undefined,
): WaterEngine {
  let tilt = 0;
  const openedAt = performance.now();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let context = createGl(canvas);
  let tier: WaterEngine["tier"] = context ? "webgl" : canvas.getContext("2d") ? "canvas2d" : "static";

  let colors = readColors(host);
  // A screen opening is an input: the water starts empty and eases up to its
  // mark — then freezes. Under reduced motion the first frame is the mark.
  let level = 0;
  let target = clampLevel(initialLevel);
  let preview = NO_PREVIEW;
  let previewTarget = NO_PREVIEW;
  let amp = 1;
  // Wave phase advances only while the water is moving, so a frozen surface
  // resumes from where it stopped rather than jumping.
  let waveTime = 0;
  let destroyed = false;

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      context?.gl.viewport(0, 0, w, h);
    }
  }

  function draw(): void {
    // Wrap the phase so a long session never feeds a huge argument into the
    // shader's mediump `sin`, where it would quantize the crest.
    const t = (waveTime * 0.001) % 3600;
    if (context) {
      const { gl, uniforms } = context;
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform1f(uniforms.t, t);
      gl.uniform1f(uniforms.lv, level);
      gl.uniform1f(uniforms.tilt, tilt);
      gl.uniform1f(uniforms.amp, amp);
      gl.uniform1f(uniforms.ar, canvas.width / Math.max(canvas.height, 1));
      gl.uniform1f(uniforms.px, 1 / Math.max(canvas.height, 1));
      gl.uniform3f(uniforms.wc, ...colors.water);
      gl.uniform3f(uniforms.cc, ...colors.crest);
      gl.uniform1f(uniforms.pv, preview);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    } else if (tier === "canvas2d") {
      draw2d(canvas, level, amp, t, colors, preview, tilt);
    }
  }

  /** One frame `dtMs` after the last (Infinity snaps). True while anything still moves. */
  function frame(dtMs: number): boolean {
    if (destroyed) return false;
    resize();
    level = easeToward(level, target, dtMs, LEVEL_HALF_LIFE_MS);
    amp = easeToward(amp, 1, dtMs, RIPPLE_HALF_LIFE_MS);
    preview =
      previewTarget === NO_PREVIEW || preview === NO_PREVIEW
        ? previewTarget
        : easeToward(preview, previewTarget, dtMs, PREVIEW_HALF_LIFE_MS);
    if (Number.isFinite(dtMs)) waveTime += dtMs;

    const moving =
      !isSettled(level, target) || !isSettled(amp, 1, RIPPLE_EPSILON) || !isSettled(preview, previewTarget);
    if (!moving) {
      // Snap, so a frozen surface is never a hair off its mark.
      level = target;
      amp = 1;
      preview = previewTarget;
    }
    draw();
    return moving;
  }

  const motion = createMotionBudget({
    frame,
    requestFrame: (callback) => requestAnimationFrame(callback),
    cancelFrame: (id) => cancelAnimationFrame(id),
    now: () => performance.now(),
    isHidden: () => document.hidden,
    reducedMotion: prefersReducedMotion,
  });

  // D-6 — no requestAnimationFrame may run while the document is hidden, and
  // coming back is not an input: one still frame, nothing moves.
  const onVisibility = (): void => {
    if (document.hidden) motion.stop();
    else motion.paintStill();
  };
  const onResize = (): void => motion.paintStill();

  const onContextLost = (event: Event): void => {
    event.preventDefault();
    motion.stop();
    context = null;
    // A canvas that acquired WebGL cannot acquire 2D. The CSS tier owns the
    // replacement, with every figure still in the DOM (D-4, D-5).
    tier = "static";
    tierChanged(tier);
  };
  canvas.addEventListener("webglcontextlost", onContextLost);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("resize", onResize);

  // The canvas box can change without the *window* changing — a layout that
  // measures its own height a tick after mount, a gauge reflowing, a font-scale
  // change. `resize()` runs inside each frame, so a frozen surface would keep
  // its old backing-store size and show a stretched frame; observing the
  // element repaints it still. No feedback loop: `resize()` writes the backing
  // store, while the observed box is set by CSS and does not move.
  const observer = new ResizeObserver(() => {
    motion.paintStill();
  });
  observer.observe(canvas);

  resize();
  motion.wake();

  return {
    get tier() { return tier; },
    setTilt(slope: number): void {
      if (destroyed || document.hidden || prefersReducedMotion() || performance.now() - openedAt >= MOTION_BUDGET_MS || !Number.isFinite(slope)) return;
      tilt = Math.max(-MAX_TILT_SLOPE, Math.min(MAX_TILT_SLOPE, slope));
      motion.paintStill();
    },
    setLevel(next: number, ripple = false): void {
      target = clampLevel(next);
      if (ripple && !prefersReducedMotion()) amp = RIPPLE_AMPLITUDE;
      motion.wake();
    },
    setPreview(next: number | null): void {
      previewTarget = next === null ? NO_PREVIEW : clampLevel(next);
      motion.wake();
    },
    refreshColors(): void {
      colors = readColors(host);
      motion.paintStill();
    },
    destroy(): void {
      canvas.removeEventListener("webglcontextlost", onContextLost);
      destroyed = true;
      motion.stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
      observer.disconnect();
      context?.gl.getExtension("WEBGL_lose_context")?.loseContext();
    },
  };
}
